import { chunk } from 'lodash';
import type { UserClient } from 'client/client';
import { Announcement, AnnouncementSurface, AnnouncementSurfaceMap, GetMyAnnouncementStateReply } from 'proto/user-manager_pb';
import { MAX_MARK_POST_IDS, RETRY_DELAY_MS, RPC_ATTEMPTS, SURFACES } from './constants';
import type { Receipt, Release, Surface } from './types';

type WireSurface = AnnouncementSurfaceMap[keyof AnnouncementSurfaceMap];

const WIRE_SURFACES: Record<Surface, WireSurface> = {
  [SURFACES.popup]: AnnouncementSurface.ANNOUNCEMENT_SURFACE_POPUP,
  [SURFACES.panel]: AnnouncementSurface.ANNOUNCEMENT_SURFACE_PANEL,
  [SURFACES.chip]: AnnouncementSurface.ANNOUNCEMENT_SURFACE_CHIP,
  [SURFACES.hint]: AnnouncementSurface.ANNOUNCEMENT_SURFACE_HINT
};
const SURFACE_BY_WIRE = new Map(Object.entries(WIRE_SURFACES).map(([surface, wire]) => [wire, surface as Surface]));

export interface AnnouncementState {
  // Epoch ms; null when user-manager sent none.
  signedUpAt: number | null;
  receipts: Receipt[];
  // The posts launched by now, newest first.
  releases: Release[];
}

const epochMs = (rfc3339: string): number | null => {
  const ms = Date.parse(rfc3339);
  return Number.isNaN(ms) ? null : ms;
};

// No roles and not paid-only is everyone, the same as no audience; no theme is the app's own colours.
const toRelease = ({ postId, launchAt, audienceRolesList, audienceEntitledOnly, theme }: Announcement.AsObject): Release => {
  const forEveryone = !audienceRolesList.length && !audienceEntitledOnly;
  return {
    postId,
    launchAt,
    ...(!forEveryone && { audience: { roles: audienceRolesList, entitledOnly: audienceEntitledOnly } }),
    ...(theme && {
      theme: {
        surface: theme.surface,
        text: theme.text,
        textMuted: theme.textMuted,
        accent: theme.accent,
        onAccent: theme.onAccent,
        glow: theme.glow
      }
    })
  };
};

/** user-manager's reply in the app's terms; a receipt with a surface this build doesn't know is dropped. */
export const toAnnouncementState = (reply: GetMyAnnouncementStateReply.AsObject): AnnouncementState => ({
  signedUpAt: epochMs(reply.signedUpAt),
  receipts: reply.receiptsList.flatMap((receipt) => {
    const surface = SURFACE_BY_WIRE.get(receipt.surface);
    const seenAt = epochMs(receipt.seenAt);
    return surface && seenAt !== null ? [{ postId: receipt.postId, surface, seenAt }] : [];
  }),
  releases: reply.announcementsList.filter((a) => epochMs(a.launchAt) !== null).map(toRelease)
});

/** Runs a call, pausing and trying again after a failure; the last failure is thrown. */
async function withRetries<T>(call: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await call();
    } catch (error) {
      if (attempt >= RPC_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }
}

export const fetchAnnouncementState = async (client: UserClient, userId: string): Promise<AnnouncementState> =>
  toAnnouncementState(await withRetries(() => client.getMyAnnouncementState(userId)));

/** Saves receipts in batches user-manager accepts. Never throws: a lost receipt only means the post may show once more. */
export async function saveReceipts(client: UserClient, userId: string, postIds: string[], surface: Surface): Promise<void> {
  await Promise.all(
    chunk(postIds, MAX_MARK_POST_IDS).map((batch) =>
      withRetries(() => client.markAnnouncements(userId, batch, WIRE_SURFACES[surface])).catch(() => undefined)
    )
  );
}
